
import React, { useState } from 'react';
import {Box,Card,CardContent,Typography,Button,Stack,IconButton,CircularProgress,Alert,Divider,Chip,TextField,Grid,FormControl,InputLabel,Select,MenuItem,Switch,FormControlLabel,Paper
} from '@mui/material';
import {ArrowBack,CheckCircle,Biotech,Business,LocalShipping,ThermostatAuto,Edit,Save,Cancel
} from '@mui/icons-material';
import { orderAssignmentAPI } from "../deliveryAgentAPI";

const LabTransfer = ({ samples, order, onBack, onTransferComplete }) => {
  // ── State ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transferred, setTransferred] = useState(false);

  // ── Lab Details Form State ────────────────────────────────────────────────
  const [isEditingLab, setIsEditingLab] = useState(true); // Start with form visible
  const [labDetails, setLabDetails] = useState({
    labName: '',
    labAddress: '',
    labContact: '',
    labEmail: '',
    estimatedDeliveryTime: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString().slice(0, 16), // 2 hours from now
  });

  // ── Transport Details State ───────────────────────────────────────────────
  const [transportDetails, setTransportDetails] = useState({
    transportMode: 'courier',
    courierService: '',
    trackingNumber: `TRK-${Date.now()}`,
    temperatureControlled: true,
    specialHandling: ''
  });

  // ── Sample List ───────────────────────────────────────────────────────────

  const sampleList =
    (samples && samples.length > 0)
      ? samples
      : (order?.sampleCollections && order.sampleCollections.length > 0)
        ? order.sampleCollections          // ← this is the actual DB array
        : [{
            test_name: order?.serviceName || 'Diagnostic Test',
            sample_type: 'Blood',
            vial_number: '–',
            sample_quality: 'good',
            storage_condition: 'refrigerated'
          }];

  // ── Handle Lab Details Changes ────────────────────────────────────────────
  const handleLabDetailChange = (field, value) => {
    setLabDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleTransportChange = (field, value) => {
    setTransportDetails(prev => ({ ...prev, [field]: value }));
  };

  // ── Validate Lab Details ──────────────────────────────────────────────────
  const validateLabDetails = () => {
    if (!labDetails.labName.trim()) {
      setError('Lab name is required');
      return false;
    }
    if (!labDetails.labAddress.trim()) {
      setError('Lab address is required');
      return false;
    }
    if (!labDetails.labContact.trim()) {
      setError('Lab contact is required');
      return false;
    }
    if (transportDetails.transportMode === 'courier' && !transportDetails.courierService.trim()) {
      setError('Courier service name is required');
      return false;
    }
    return true;
  };

  // ── Save Lab Details ──────────────────────────────────────────────────────
  const handleSaveLabDetails = () => {
    if (validateLabDetails()) {
      setIsEditingLab(false);
      setError(null);
    }
  };

  // ── Send to Lab ───────────────────────────────────────────────────────────
  const handleSendToLab = async () => {
    try {
      // Validate lab details first
      if (!validateLabDetails()) {
        return;
      }

      setLoading(true);
      setError(null);

      const userId = sessionStorage.getItem('userId');

      // Build payload
      const payload = {
        samples: [order.id], // array of booking mongo IDs
        transferData: {
          labName: labDetails.labName,
          labAddress: labDetails.labAddress,
          labContact: labDetails.labContact,
          labEmail: labDetails.labEmail || undefined,
          transportMode: transportDetails.transportMode,
          courierService: transportDetails.transportMode === 'courier' 
            ? transportDetails.courierService 
            : null,
          trackingNumber: transportDetails.trackingNumber,
          estimatedDeliveryTime: labDetails.estimatedDeliveryTime,
          temperatureControlled: transportDetails.temperatureControlled,
          specialHandling: transportDetails.specialHandling || null,
          transferredBy: userId
        }
      };

      console.log(' Sending to lab:', payload);

      const response = await orderAssignmentAPI.transferToLab(
        payload.samples,
        payload.transferData
      );

      if (response.data.success) {
        setTransferred(true);

        // Tell the dashboard the transfer is done
        setTimeout(() => {
          onTransferComplete({
            labName: labDetails.labName,
            labAddress: labDetails.labAddress,
            labContact: labDetails.labContact,
            trackingNumber: transportDetails.trackingNumber,
            transferredAt: new Date().toISOString(),
            transportMode: transportDetails.transportMode,
            temperatureControlled: transportDetails.temperatureControlled
          });
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to send samples to lab');
      }
    } catch (err) {
      console.error(' Transfer error:', err);
      setError(err.response?.data?.message || 'Error sending samples to lab');
    } finally {
      setLoading(false);
    }
  };

  // ── Format Sample Quality Badge ───────────────────────────────────────────
  const getQualityColor = (quality) => {
    switch (quality?.toLowerCase()) {
      case 'excellent': return 'success';
      case 'good': return 'success';
      case 'acceptable': return 'info';
      case 'poor': return 'error';
      case 'hemolyzed': return 'warning';
      case 'lipemic': return 'warning';
      case 'clotted': return 'error';
      case 'insufficient': return 'error';
      case 'contaminated': return 'error';
      default: return 'default';
    }
  };

  // ── Format Storage Condition ──────────────────────────────────────────────
  const getStorageIcon = (condition) => {
    if (condition?.toLowerCase().includes('refrigerated')) return '❄️';
    if (condition?.toLowerCase().includes('frozen')) return '🧊';
    if (condition?.toLowerCase().includes('room')) return '🌡️';
    return '📦';
  };

  const isValidContactNumber = (value) => {
    // Remove +91 and spaces
    const cleanedValue = value.replace(/^(\+91\s?)?/, '').replace(/\s/g, '');
  
    // Check if exactly 10 digits
    return /^\d{10}$/.test(cleanedValue);
  };
  
  // RENDER
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', pb: 4 }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={onBack} disabled={loading || transferred}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Send Samples to Lab
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order #{order?.orderId}
          </Typography>
        </Box>
      </Stack>

      {/* ── Important Instructions ──────────────────────────────────────── */}
      <Alert severity="warning" icon={false} sx={{ mb: 3, bgcolor: '#fff3cd', border: '2px solid #ffc107' }}>
        <Typography variant="body2" fontWeight={600} gutterBottom color="#856404">
           IMPORTANT INSTRUCTIONS
        </Typography>
        <Typography variant="body2" color="#856404" component="div">
          <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
            <li><strong>DO NOT use dummy or placeholder data</strong></li>
            <li>Enter the <strong>actual laboratory name and address</strong> where samples will be sent</li>
            <li>Provide <strong>real contact information</strong> for the receiving lab</li>
            <li>Enter the <strong>actual courier service name</strong> if using courier delivery</li>
            <li>Verify all information before submitting</li>
          </ul>
        </Typography>
      </Alert>

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Success Banner ──────────────────────────────────────────────── */}
      {transferred && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
             Samples sent to {labDetails.labName} successfully!
          </Typography>
          <Typography variant="body2">
            Status updated to <strong>Sample Sent to Lab</strong>
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Tracking: {transportDetails.trackingNumber}
          </Typography>
        </Alert>
      )}

      {/* ── Laboratory Details Card ─────────────────────────────────────── */}
      <Card sx={{ mb: 3, border: isEditingLab ? '2px solid #006D77' : '1px solid #ddd' }}>
        <CardContent>
          <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between" 
            mb={2}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Business sx={{ color: '#006D77', fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600} color="#006D77">
                Laboratory Details
              </Typography>
            </Stack>
            
            {!transferred && !isEditingLab && (
              <IconButton 
                onClick={() => setIsEditingLab(true)} 
                size="small"
                sx={{ color: '#006D77' }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {isEditingLab && !transferred ? (
            // ── EDIT MODE ──────────────────────────────────────────────────
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Laboratory Name *"
                  fullWidth
                  value={labDetails.labName}
                  onChange={(e) => handleLabDetailChange('labName', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., Ayuja Central Laboratory"
                  required
                  helperText="Enter the actual lab name - DO NOT use dummy data"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Laboratory Address *"
                  fullWidth
                  multiline
                  rows={2}
                  value={labDetails.labAddress}
                  onChange={(e) => handleLabDetailChange('labAddress', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., 123, Lab Road, Medical District, Hyderabad – 500 032"
                  required
                  helperText="Complete address with landmarks and pincode"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Number *"
                  fullWidth
                  value={labDetails.labContact}
                  onChange={(e) =>
                    handleLabDetailChange('labContact', e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  placeholder="+91 9876543210"
                  required
                  error={
                    labDetails.labContact &&
                    !isValidContactNumber(labDetails.labContact)
                  }
                  helperText={
                    labDetails.labContact &&
                    !isValidContactNumber(labDetails.labContact)
                      ? "Enter a valid 10-digit number (+91 is optional)"
                      : "Lab's phone number"
                  }
                />
              </Grid>


              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email (Optional)"
                  fullWidth
                  value={labDetails.labEmail}
                  onChange={(e) => handleLabDetailChange('labEmail', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="lab@example.com"
                  type="email"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Estimated Delivery Time"
                  type="datetime-local"
                  fullWidth
                  value={labDetails.estimatedDeliveryTime}
                  onChange={(e) => handleLabDetailChange('estimatedDeliveryTime', e.target.value)}
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsEditingLab(false)}
                    startIcon={<Cancel />}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveLabDetails}
                    startIcon={<Save />}
                    sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005561' } }}
                  >
                    Save Details
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          ) : (
            // ── VIEW MODE ──────────────────────────────────────────────────
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Lab Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {labDetails.labName || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Address
                </Typography>
                <Typography variant="body2">
                  {labDetails.labAddress || '—'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Contact
                </Typography>
                <Typography variant="body2">
                  <a 
                    href={`tel:${labDetails.labContact}`} 
                    style={{ color: '#006D77', textDecoration: 'none', fontWeight: 500 }}
                  >
                    {labDetails.labContact || '—'}
                  </a>
                </Typography>
              </Grid>

              {labDetails.labEmail && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {labDetails.labEmail}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                  Estimated Delivery
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {new Date(labDetails.estimatedDeliveryTime).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>



{/* ── Collected Samples Card ──────────────────────────────────────── */}
<Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Biotech sx={{ color: '#006D77', fontSize: 24 }} />
            <Typography variant="h6" fontWeight={600}>
              Collected Samples
            </Typography>
            <Chip
              label={`${sampleList.length} sample${sampleList.length !== 1 ? 's' : ''}`}
              size="small"
              color="info"
            />
          </Stack>

          {sampleList.map((sample, idx) => {
            // ── resolve every field once: snake_case OR camelCase, treat "" as missing ──
            const val = (a, b) => {
              const v = a ?? b;
              return (typeof v === 'string' && v.trim() === '') ? null : v;
            };

            const testName         = val(sample.test_name,        sample.testName)        || `Sample ${idx + 1}`;
            const testPrice        = val(sample.test_price,       sample.testPrice);       // keep 0 as valid
            const sampleType       = val(sample.sample_type,      sample.sampleType);
            const sampleQuality    = val(sample.sample_quality,   sample.sampleQuality)   || 'good';
            const vialNumber       = val(sample.vial_number,      sample.vialNumber);
            const collectionTime   = val(sample.collection_time,  sample.collectionTime);
            const patientCondition = val(sample.patient_condition,sample.patientCondition);
            const fastingStatus    = val(sample.fasting_status,   sample.fastingStatus);
            const specialNotes     = val(sample.special_notes,    sample.specialNotes);
            const temperature      = val(sample.temperature,      sample.temperature);
            const storageCondition = val(sample.storage_condition,sample.storageCondition);
            const collectedBy      = val(sample.collected_by,     sample.collectedBy);
            const sequenceNumber   = sample.sequence_number       ?? sample.sequenceNumber  ?? null;
            const status           = val(sample.status,           sample.status);
            const createdAt        = val(sample.created_at,       sample.createdAt);

            // ── status badge colour ─────────────────────────────────────
            const statusColor = { collected: 'info', transferred: 'warning', received_at_lab: 'success' };

            // ── human-readable status ───────────────────────────────────
            const fmtStatus = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;

            // ── capitalise first letter ─────────────────────────────────
            const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : null;

            return (
              <Box key={idx}>
                {idx > 0 && <Divider sx={{ my: 2 }} />}

                {/* ── Header: #seq · testName · quality · status ── */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1.5}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {sequenceNumber !== null && (
                      <Chip
                        label={`#${sequenceNumber}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#006D77', color: '#006D77', fontWeight: 600 }}
                      />
                    )}
                    <Typography variant="body1" fontWeight={600}>
                      {testName}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={0.75}>
                    <Chip
                      label={sampleQuality.toUpperCase()}
                      size="small"
                      color={getQualityColor(sampleQuality)}
                    />
                    {status && (
                      <Chip
                        label={fmtStatus(status)}
                        size="small"
                        color={statusColor[status] || 'default'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Stack>

                {/* ── Details Grid ─────────────────────────────────── */}
                <Grid container spacing={1.5}>

                  {/* test_price */}
                  {testPrice !== null && testPrice !== undefined && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Test Price</Typography>
                        <Typography variant="body2" fontWeight={600} color="#006D77">
                          ₹{Number(testPrice).toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* sample_type */}
                  {sampleType && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Sample Type</Typography>
                        <Typography variant="body2" fontWeight={500}>{sampleType}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* vial_number */}
                  {vialNumber && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Vial Number</Typography>
                        <Typography variant="body2" fontWeight={500}>{vialNumber}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* storage_condition */}
                  {storageCondition && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Storage</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {getStorageIcon(storageCondition)} {cap(storageCondition)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* patient_condition */}
                  {patientCondition && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Patient Condition</Typography>
                        <Typography variant="body2" fontWeight={500}>{cap(patientCondition)}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* fasting_status */}
                  {fastingStatus && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Fasting Status</Typography>
                        <Typography variant="body2" fontWeight={500}>{cap(fastingStatus)}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* temperature */}
                  {temperature && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Temperature</Typography>
                        <Typography variant="body2" fontWeight={500}>{temperature}°C</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* collected_by */}
                  {collectedBy && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Collected By</Typography>
                        <Typography variant="body2" fontWeight={500}>{collectedBy}</Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* collection_time */}
                  {collectionTime && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Collection Time</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date(collectionTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* created_at — only when it meaningfully differs from collection_time */}
                  {createdAt && (
                    new Date(createdAt).toISOString().slice(0, 16) !== new Date(collectionTime || 0).toISOString().slice(0, 16)
                  ) && (
                    <Grid item xs={6} sm={4}>
                      <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8f9fa' }}>
                        <Typography variant="caption" color="text.secondary">Record Created</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {new Date(createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {/* special_notes — full-width alert */}
                  {specialNotes && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Special Notes
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          {specialNotes}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })}
        </CardContent>
      </Card>




      {/* ── Transport Details Card ──────────────────────────────────────── */}
      {!transferred && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <LocalShipping sx={{ color: '#006D77', fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600}>
                Transport Details
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transport Mode</InputLabel>
                  <Select
                    value={transportDetails.transportMode}
                    onChange={(e) => handleTransportChange('transportMode', e.target.value)}
                    label="Transport Mode"
                  >
                    <MenuItem value="courier">Courier Service</MenuItem>
                    <MenuItem value="hand_delivery">Hand Delivery</MenuItem>
                    <MenuItem value="bike">Bike</MenuItem>
                    <MenuItem value="car">Car</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {transportDetails.transportMode === 'courier' && (
                <Grid item xs={12}>
                  <TextField
                    label="Courier Service Name *"
                    fullWidth
                    value={transportDetails.courierService}
                    onChange={(e) => handleTransportChange('courierService', e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="e.g., Blue Dart, DTDC, FedEx"
                    required
                    helperText="Enter actual courier service name - DO NOT use dummy data"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  label="Tracking Number"
                  fullWidth
                  value={transportDetails.trackingNumber}
                  onChange={(e) => handleTransportChange('trackingNumber', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled
                  helperText="Auto-generated tracking number"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={transportDetails.temperatureControlled}
                      onChange={(e) => handleTransportChange('temperatureControlled', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <ThermostatAuto fontSize="small" />
                      <Typography variant="body2">
                        Temperature Controlled Transport
                      </Typography>
                    </Stack>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Special Handling Instructions (Optional)"
                  fullWidth
                  multiline
                  rows={2}
                  value={transportDetails.specialHandling}
                  onChange={(e) => handleTransportChange('specialHandling', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., Keep refrigerated, Handle with care, Fragile"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Temperature Reminder ───────────────────────────────────────── */}
      {transportDetails.temperatureControlled && !transferred && (
        <Alert severity="info" sx={{ mb: 3 }} icon={false}>
          <Typography variant="body2">
            🌡️ <strong>Temperature Control Active:</strong> Samples will be transported 
            in temperature-controlled packaging. Ensure samples remain properly stored 
            until pickup.
          </Typography>
        </Alert>
      )}

      {/* ── Action Buttons ──────────────────────────────────────────────── */}
      {!transferred ? (
        <Stack spacing={2}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSendToLab}
            disabled={loading || isEditingLab}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : <LocalShipping />
            }
            sx={{ 
              py: 1.8, 
              bgcolor: '#006D77', 
              '&:hover': { bgcolor: '#005561' },
              '&.Mui-disabled': { bgcolor: '#ccc' }
            }}
          >
            <Typography variant="body1" fontWeight={700}>
              {loading 
                ? 'SENDING TO LAB...' 
                : `SEND ${sampleList.length} SAMPLE${sampleList.length !== 1 ? 'S' : ''} TO LAB`
              }
            </Typography>
          </Button>

          {isEditingLab && (
            <Alert severity="warning" sx={{ mb: 0 }}>
              Please save laboratory details before sending samples
            </Alert>
          )}

          <Button 
            variant="outlined" 
            size="large" 
            fullWidth 
            onClick={onBack} 
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            Back
          </Button>
        </Stack>
      ) : (
        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          disabled
          startIcon={<CheckCircle />}
          sx={{ py: 1.8, bgcolor: '#28a745' }}
        >
          Redirecting to completion…
        </Button>
      )}
    </Box>
  );
};

export default LabTransfer;